// Display helpers for billing copy. Plain functions so views and tests share
// the exact wording.
import type { BillingPrice, SubscriptionState } from "@/types/billing";

/** "49 € / month" from an amount in minor units. */
export function formatPrice(price: BillingPrice | null | undefined): string {
  if (!price) return "—";
  const amount = price.amountMinor / 100;
  const currency = (price.currency || "eur").toUpperCase();
  let formatted: string;
  try {
    formatted = new Intl.NumberFormat("en-IE", {
      style: "currency",
      currency,
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    formatted = `${amount} ${currency}`;
  }
  return `${formatted} / ${formatInterval(price.interval)}`;
}

export function formatInterval(interval: string): string {
  switch (interval) {
    case "month":
      return "month";
    case "year":
      return "year";
    default:
      return interval;
  }
}

/** "unlimited" or the number, for a limit that may be null. */
export function formatLimit(limit: number | null | undefined): string {
  return limit === null || limit === undefined ? "unlimited" : String(limit);
}

/** Long, unambiguous date for period ends and grace deadlines. */
export function formatBillingDate(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

export function describeSubscriptionState(state: SubscriptionState): string {
  switch (state) {
    case "unlimited":
      return "No limits";
    case "none":
      return "No subscription";
    case "pending":
      return "Waiting for payment";
    case "active":
      return "Active";
    case "past_due":
      return "Payment failed";
    case "locked":
      return "Locked";
    case "cancelled":
      return "Cancelled";
    default:
      return state;
  }
}

/** True when a limit exists and usage has reached it. */
export function isAtLimit(usage: number, limit: number | null | undefined): boolean {
  return limit !== null && limit !== undefined && usage >= limit;
}

export function namespaceLimitMessage(limit: number): string {
  return `Your plan allows ${limit} namespace${limit === 1 ? "" : "s"}. Upgrade your plan to add more.`;
}

export function seatLimitMessage(usage: number, limit: number): string {
  return `Seat limit reached (${usage} of ${limit}). Upgrade your plan to invite more people.`;
}
